import type { ReactNode } from 'react';
import { useFormat } from '@paige/format';
import { FlowGlyph } from './FlowGlyph';
import './incidents.css';
import type { AdminFlow, FlowIncident, FlowPlace } from './flow-types';
import { testsWords, verdictOf, type Verdict, type VerdictTone } from './flow-verdict';

const GLYPH: Record<VerdictTone, 'stuck' | 'slow' | 'idle'> = {
  red: 'stuck',
  amber: 'slow',
  clear: 'idle',
};

/** The colours on the picture, each with its one meaning. */
const LEGEND: { tone: string; words: string }[] = [
  { tone: 'stuck', words: 'Needs us now' },
  { tone: 'slow', words: 'Slow, or Paige is retrying by itself' },
  { tone: 'held', words: 'Held by design, or the customer’s move' },
  { tone: 'moving', words: 'Flowing' },
];

export function FlowLegend(): ReactNode {
  return (
    <ul className="fl-legend" aria-label="What the colours mean">
      {LEGEND.map((l) => (
        <li key={l.tone} className={`fl-legend__item fl-legend__item--${l.tone}`}>
          <span className="fl-legend__swatch" aria-hidden="true" />
          {l.words}
        </li>
      ))}
    </ul>
  );
}

/** The sentence, as a button to what it names when it names something. */
function Headline({
  verdict: v,
  onOpen,
}: {
  verdict: Verdict;
  onOpen: (place: FlowPlace, incident: string | null) => void;
}): ReactNode {
  const place = v.place;
  if (!place) return <p className="fl-verdict__line">{v.headline}</p>;
  return (
    <button
      type="button"
      className="fl-verdict__line"
      onClick={() => onOpen(place, v.incident?.id ?? null)}
    >
      {v.headline}
    </button>
  );
}

/**
 * Is anything wrong right now: one sentence and one colour for the whole platform, the worst thing first, a
 * button to it; what Paige is doing; and what "Hide test companies" left out, said on the strip.
 */
export function FlowVerdict({
  flow,
  incidents,
  onOpen,
}: {
  flow: AdminFlow;
  incidents: FlowIncident[] | null;
  /** Opens the drawer at a stage, on an incident. */
  onOpen: (place: FlowPlace, incident: string | null) => void;
}): ReactNode {
  const f = useFormat();
  if (!incidents) {
    return (
      <section className="fl-verdict" data-verdict="reading" aria-live="polite">
        <p className="fl-verdict__line">Checking every stage</p>
      </section>
    );
  }
  const v = verdictOf(f, flow, incidents);
  return (
    <section
      className={`fl-verdict fl-verdict--${v.tone}`}
      data-verdict={v.tone}
      aria-live="polite"
    >
      <FlowGlyph tone={GLYPH[v.tone]} />
      <div className="fl-verdict__text">
        <Headline verdict={v} onOpen={onOpen} />
        <p className="fl-verdict__detail">
          {v.detail} {testsWords(f, flow)}
        </p>
      </div>
      <FlowLegend />
    </section>
  );
}
